import React, { useContext, useMemo } from "react";
import { Box } from "@mui/material";
import { gql, useQuery } from "@apollo/client";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { deliveryAvailableAt } from "../../apollo/server";
import UserContext from "../../context/User";
import AddressSelector from "../Header/AddressSelector";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, RADIUS, TYPE } from "../../utils/designTokens";
import { pointOf } from "./pickupText";

const DELIVERY_AVAILABLE_AT = gql`${deliveryAvailableAt}`;

const iconFor = (label) => {
  const l = String(label || "").toLowerCase();
  if (l === "apartment") return ApartmentRoundedIcon;
  if (l === "work" || l === "office") return WorkOutlineRoundedIcon;
  if (l === "house" || l === "home") return HomeOutlinedIcon;
  return PlaceOutlinedIcon;
};

/**
 * "Prefer delivery?": the customer's other saved addresses that DO get
 * delivery (asked of the server, same rule as the list), each with "Use
 * this", then "Change or add address". The rows hide when none qualify.
 */
function PreferDelivery({ currentAddressId, currentAddress, onUse }) {
  const { profile, isLoggedIn } = useContext(UserContext);
  const candidates = useMemo(
    () =>
      (isLoggedIn ? profile?.addresses || [] : [])
        // Not the address in use, whether it was picked from the saved list or typed in.
        .filter((a) => String(a._id) !== String(currentAddressId) && a.deliveryAddress !== currentAddress && pointOf(a))
        .slice(0, 10),
    [isLoggedIn, profile?.addresses, currentAddressId, currentAddress]
  );
  const { data } = useQuery(DELIVERY_AVAILABLE_AT, {
    variables: { points: candidates.map(pointOf) },
    skip: candidates.length === 0,
    fetchPolicy: "cache-and-network",
  });
  const answers = data?.deliveryAvailableAt || [];
  const delivering = candidates.filter((_, i) => answers[i] === true);

  return (
    <Box component="section" aria-labelledby={delivering.length ? "prefer-delivery-title" : undefined} sx={{ maxWidth: 720 }}>
      {delivering.length > 0 && (
        <>
          <Box component="h2" id="prefer-delivery-title" sx={{ ...TYPE.sectionTitle, fontSize: 20, m: 0, color: C.ink }}>
            Prefer delivery?
          </Box>
          <Box component="p" sx={{ ...TYPE.body, m: 0, mt: 0.5, mb: 1.5, color: C.muted }}>
            These saved addresses have restaurants that deliver
          </Box>
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 1.25 }}>
            {delivering.map((a) => {
              const Icon = iconFor(a.label);
              return (
                <Box component="li" key={a._id} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: RADIUS.menuCard, border: `1px solid ${C.border}`, bgcolor: C.bg }}>
                  <Box sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: "12px", bgcolor: C.surface, color: C.ink, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon aria-hidden="true" sx={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ fontFamily: FONT_BODY, fontSize: 15, fontWeight: 700, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {a.label || "Saved address"}
                    </Box>
                    <Box sx={{ fontFamily: FONT_BODY, fontSize: 13, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {a.deliveryAddress}
                    </Box>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onUse(a)}
                    aria-label={`Use ${a.label || "this address"} for delivery`}
                    sx={{
                      ...BUTTON_RESET,
                      ...FOCUS_RING,
                      flexShrink: 0,
                      minHeight: 44,
                      px: 2,
                      borderRadius: RADIUS.pill,
                      bgcolor: C.redTint,
                      color: C.red,
                      fontFamily: FONT_BODY,
                      fontSize: 14,
                      fontWeight: 700,
                      "&:hover": { bgcolor: C.redSoft },
                    }}
                  >
                    Use this
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}
      <AddressSelector
        renderTrigger={(props) => (
          <Box
            component="button"
            type="button"
            {...props}
            sx={{
              ...BUTTON_RESET,
              ...FOCUS_RING,
              mt: delivering.length ? 1.5 : 0,
              width: "100%",
              minHeight: 48,
              borderRadius: RADIUS.pill,
              border: `1.5px solid ${C.borderStrong}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              fontFamily: FONT_BODY,
              fontSize: 15,
              fontWeight: 700,
              color: C.ink,
              "&:hover": { borderColor: C.ink, bgcolor: C.surface },
            }}
          >
            <AddRoundedIcon aria-hidden="true" sx={{ fontSize: 20 }} />
            Change or add address
          </Box>
        )}
      />
    </Box>
  );
}

export default PreferDelivery;
